/**
 * The mid-call recording toggle, as a state machine rather than a checkbox.
 *
 * ===========================================================================
 * WHY THIS IS NOT A CHECKBOX
 *
 * `PUT /v1/rooms/{id}/recording` is a compare-and-set on a DESIRED value. The
 * recorder converges to it afterwards, and the two facts live in two different
 * fields — `desired` and `actual` — precisely because they are not the same
 * fact. A control that snaps to its new position on click would be claiming
 * the recorder had already stopped, which is a privacy lie for as long as the
 * convergence takes.
 *
 * Measured against the deployed controller on 2026-08-09 (session
 * `s_20260809021055_PPLiyiga6W`, one live MICROPHONE publisher):
 *
 *   PUT none @rev1 -> 202 {desired:none, actual:none, revision:2,
 *                          applied_revision:1, transition:"disabling",
 *                          active_capture_count:0}
 *   +1.4s              {desired:none, actual:none, revision:2,
 *                          applied_revision:2, transition:"stable"}
 *
 * Note what the accept response says: `actual` had ALREADY dropped to `none`
 * and `active_capture_count` to 0 while the recorder was still closing the
 * source — `transition:"disabling"` and `applied_revision < revision` were the
 * only honest signals in that window. So a UI reading `actual` alone would
 * have told a room "audio is no longer being stored" before that was true.
 * That is the single most important reason `transition` wins over `actual`
 * in `recordingPresentation()`, and it is why `converging()` below also treats
 * `applied_revision < revision` as unsettled.
 *
 * ===========================================================================
 * THE THREE THINGS THAT ARE NOT THE POLICY
 *
 * 1. WHETHER THIS BROWSER MAY CHANGE IT. Only the room creator holds the
 *    capability (`control_token`, BFF migration 0006). Everyone else still
 *    sees the state — see `privacy.tsx` for why that is deliberate.
 * 2. WHETHER THE DEPLOYMENT SUPPORTS CHANGING IT AT ALL. The endpoint is
 *    "deliberately unavailable on stock egress, comparison mode, or with the
 *    durable audio graph disabled", answered as 409
 *    `recording_policy_backend_unsupported`. Current snapshots expose
 *    `mutable`; the error constant remains for rolling-deployment fallback.
 * 3. WHETHER THE LAST ATTEMPT FAILED. `policy.error` is the controller's
 *    TERMINAL reconcile failure; an `ApiError` from our own request is a
 *    different failure with a different remedy. Both are surfaced, separately.
 *
 * ===========================================================================
 * THIS FILE IS THE AUDIO CHANNEL ONLY, AND DELIBERATELY KNOWS NOTHING ABOUT
 * VIDEO.
 *
 * Retained video is a child of the unified public policy, with its own
 * convergence state. Its state machine lives in
 * `video-control.ts`, which imports the generic pieces below (`fenceConverging`,
 * `pollIntervalFor`, `RetentionLevel`) and adds what only video has. The import
 * runs one way — video presentation knows about audio, audio does not know
 * about video — because the two-channel combination is a fact about arrival, and
 * making this module depend on the newer one would create a cycle for nothing.
 */

import type { RoomAudioRecordingPolicy, StoredAudioMode } from '@era/shared'

/**
 * The 409 code the control plane answers when live policy changes are not
 * available on this deployment (`control/era_control/api/rooms.py:286`).
 * It is a configuration fact about the whole controller, not about this room.
 */
export const RECORDING_UNSUPPORTED = 'recording_policy_backend_unsupported'

/** The 409 code for a lost compare-and-set race (`api/rooms.py:314`). */
export const RECORDING_CONFLICT = 'recording_policy_revision_conflict'

/* -------------------------------------------------------------------------- */
/* the part both storage channels share                                        */
/* -------------------------------------------------------------------------- */

/**
 * The shape of a compare-and-set policy snapshot, reduced to the three fields
 * that decide whether it has settled.
 *
 * The unified parent exposes audio and selected-video child convergence in one
 * snapshot. Generalising this fence logic keeps their privacy-safe revision
 * checks consistent while their presentation states remain distinct: video
 * can be storing and reconfiguring at the same time, which audio cannot.
 *
 * `transition` is typed as `string` rather than a union so a caller can pass
 * either enum without a cast. The in-flight words are supplied by the caller,
 * which is the only part that actually varies.
 */
export interface PolicyFence {
  revision: number
  applied_revision: number
  transition: string
}

/**
 * Is this policy ARMED — asking for capture, capturing nothing, and NOT MOVING?
 *
 * ===========================================================================
 * THIS IS THE SHAPE THE JOIN CASE ACTUALLY PRODUCES, MEASURED 2026-08-09
 *
 * A room created through the app, then one publisher joined, audio policy
 * sampled every ~2.4s:
 *
 *   at creation   desired=audio actual=none  rev 1/1 stable  active=0
 *   t+2.5s        desired=audio actual=audio rev 1/1 stable  active=1
 *   …stable at actual=audio for the whole call…
 *   t+34.4s       desired=audio actual=none  rev 1/1 stable  active=0
 *
 * The controller NEVER reports `enabling` for a join and NEVER advances the
 * revision. `desired` is already `audio` at creation; `actual` simply flips
 * about 2.5s after an eligible microphone track exists, and back when the last
 * one goes. So the first row is not the beginning of a transition — it is a
 * STEADY STATE, and it persists indefinitely in a room where nobody unmutes.
 *
 * Rendering that as `starting` is what produced the operator's report that
 * "audio recording on join seems stuck on starting". It is not slow:
 * convergence is ~2.5s once a track exists. It was MISLABELLED, and the label
 * promised motion that was never coming.
 *
 * ===========================================================================
 * WHY ALL FIVE CONDITIONS, AND WHY `desired != actual` IS NOT THE TEST
 *
 * `desired != actual` on its own is ALSO true throughout a genuine mid-call
 * enable, which is a real transition (the platform team confirmed 2026-08-09:
 * the PUT returns 202 BEFORE a recorder is assigned, the revision advances,
 * `transition` becomes `enabling`, and it can later become `failed`). Calling
 * that armed would delete the one state the creator most needs to see moving.
 * So each condition below excludes a different genuine transition:
 *
 *  - `wantsCapture` — a policy that is not asking for capture is `off` or
 *    `stopping`, never armed.
 *  - `!capturing` — `actual` already reports ownership; there is nothing to
 *    arm for.
 *  - `activeCount === 0` — a non-zero count is the controller's own "this
 *    attempt may own a recorder side effect". Armed's whole claim is that
 *    nothing is being captured right now, and this is the only field that can
 *    contradict it.
 *  - `transition === 'stable'` — an EQUALITY, not "is not in the in-flight
 *    set". `failed` is terminal and outranks everything, and a transition word
 *    this build has never heard of has to fall through to the conservative
 *    reading rather than be quietly treated as at rest.
 *  - `applied_revision >= revision` — the fence. A revision this browser wrote
 *    that the recorder has not acted on is motion, whatever `transition` says;
 *    the deployed controller was measured reporting a stale `stable` with the
 *    fence unsatisfied (FINDINGS #42).
 *
 * ARMED IS NOT AN ALL-CLEAR. Nothing is being captured at this instant, which
 * is true, but the state ends WITHOUT any further request, decision or prompt —
 * a microphone appears and ~2.5s later audio is being stored. That is faster
 * than a person can react, so every string derived from this state has to state
 * the trigger rather than the current silence. See `audioChannelStatus`.
 */
export function fenceArmed(
  fence: PolicyFence,
  wantsCapture: boolean,
  capturing: boolean,
  activeCount: number,
): boolean {
  if (!wantsCapture) return false
  if (capturing) return false
  if (activeCount > 0) return false
  if (fence.transition !== 'stable') return false
  return fence.applied_revision >= fence.revision
}

/**
 * Has this policy NOT settled yet?
 *
 * Two independent signals, and both are needed. `transition` is the
 * controller's own word for it; `applied_revision < revision` is the fence
 * that says the revision this browser just wrote has not been acted on yet.
 * A build that reported one without the other would still be caught — and on
 * 2026-08-09 the deployed controller did exactly that on the audio channel
 * (FINDINGS #42), which is why neither signal is trusted alone here.
 *
 * `failed` is checked before either: it is TERMINAL, not in flight. Nothing
 * converges without a new request, so reporting it as movement is what would
 * leave a 1.5s poll running behind an error for the rest of the call.
 */
export function fenceConverging(fence: PolicyFence, inFlight: ReadonlySet<string>): boolean {
  if (fence.transition === 'failed') return false
  if (inFlight.has(fence.transition)) return true
  return fence.applied_revision < fence.revision
}

/**
 * How much of a channel is being stored, collapsed to three certainties plus
 * one defect state.
 *
 * This is the vocabulary the two-channel indicator combines over, and the
 * collapse is where the conservative rule is applied ONCE rather than at every
 * call site: `unsettled` covers starting, stopping AND terminal-failed,
 * because all three mean "assume it is being stored". `unknown` is not a
 * policy state at all — it means this build cannot observe the channel, and it
 * exists so that "not reported" can never be rendered as "not stored".
 *
 * `armed` is the fifth, and it is the only one that is neither. It means the
 * policy is switched on and nothing is being captured at this instant — TRUE,
 * and true indefinitely in a room where nobody unmutes. It is not folded into
 * `off` because the state ends with no request, no prompt and no decision:
 * a microphone appears and storage begins. It is not folded into `unsettled`
 * because there is nothing in flight, so "assume it is being stored" would be
 * a false alarm and a poll that never stops. Everything rendered from it has
 * to name the TRIGGER instead of the current silence.
 */
export type RetentionLevel = 'off' | 'armed' | 'unsettled' | 'on' | 'unknown'

export type RecordingPresentation = 'live' | 'starting' | 'armed' | 'stopping' | 'off' | 'failed'

/**
 * The audio channel's contribution to the combined statement.
 *
 * `failed` folds into `unsettled` on purpose. It is a different thing to SHOW —
 * it gets its own colour, its own sentence and the only re-drive button — but
 * as an answer to "is my voice being stored", "we do not know" and "it is on
 * its way to stopping" are the same answer: assume yes.
 */
export function audioRetentionLevel(presentation: RecordingPresentation): RetentionLevel {
  switch (presentation) {
    case 'live':
      return 'on'
    case 'starting':
    case 'stopping':
    case 'failed':
      return 'unsettled'
    // Its own level, on purpose. Folding it into `unsettled` would put a room
    // where nobody has ever unmuted permanently under "assume it is being
    // stored", which is the cry-wolf version of this indicator; folding it into
    // `off` would print "Nothing stored" over a policy that starts storing by
    // itself. Neither is available, so it is a level.
    case 'armed':
      return 'armed'
    case 'off':
      return 'off'
  }
}

/**
 * Six states, because desired policy and actual capture do not change in one
 * atomic instant — and because one of the shapes that looks like that interval
 * is not an interval at all.
 *
 * The controller admits the revision first, then its worker starts/stops each
 * eligible microphone or screen-share-audio source. Showing that interval as
 * already complete would be a privacy lie, so starting and stopping are
 * first-class states. A failed reconcile is first-class for the same reason.
 *
 * `armed` is the sixth and it is the opposite problem: a shape that looks
 * mid-flight and is not. See `fenceArmed` for the measurement that separates
 * them. The order below is what keeps them separate — `failed` first because it
 * is terminal, `converging` second because it is the only real motion, and only
 * then may the settled-and-diverged shape be read as at rest.
 */
export function recordingPresentation(policy: RoomAudioRecordingPolicy): RecordingPresentation {
  if (policy.transition === 'failed') return 'failed'
  // A capture attempt in starting/start_unknown/stopping contributes to
  // `actual=audio`, so transition has to win. Otherwise an accepted enable can
  // look live before the recorder has applied it, or a disable can look done
  // while sources are still closing.
  //
  // `converging()` also catches `applied_revision < revision`, which matters
  // more than it looks: the deployed controller's own 202 for a stop already
  // reported `actual:"none"` and `active_capture_count:0` while the recorder
  // was still closing the source (measured above). Believing `actual` there
  // would have told the room "audio is no longer stored" before it was true.
  if (converging(policy)) return policy.desired === 'audio' ? 'starting' : 'stopping'
  if (policy.desired === 'audio') {
    if (policy.actual === 'audio') return 'live'
    // Settled, asking for audio, capturing nothing. `armed` if EVERY condition
    // in `fenceArmed` holds; otherwise this falls through to `starting`, which
    // is the conservative reading and covers the two shapes armed refuses:
    // a non-zero `active_capture_count` (an attempt may already own a recorder
    // side effect) and a `transition` word this build does not recognise.
    return audioArmed(policy) ? 'armed' : 'starting'
  }
  // `active_capture_count` is the controller's count of attempts that "may own
  // a recorder side effect". A settled `none` with a non-zero count is either a
  // source still closing or a leaked recorder; both mean audio may still be
  // being written, and "off" is the one claim this indicator must never make
  // when it is not true.
  if (policy.actual === 'audio' || policy.active_capture_count > 0) return 'stopping'
  return 'off'
}

/**
 * One sentence answering the only question that matters to a participant:
 * is my voice being stored right now?
 *
 * It exists separately from the indicator's two-word label because the pill
 * has room for two words and this has to be unambiguous. It is the button's
 * accessible name and the panel's headline, so a screen reader and a glance
 * get the same answer.
 *
 * Both converging states answer "possibly yes". That is the conservative
 * direction and the only defensible one: a person deciding whether to say
 * something out loud is harmed by a false "no" and merely inconvenienced by a
 * cautious "maybe".
 */
/**
 * The armed sentence, written ONCE, because it is the copy this whole state
 * exists to deliver and it appears in three places — the announced live region,
 * the panel headline, and the button's accessible name. A second copy is a
 * second thing to get wrong, and the two would drift in exactly the direction
 * that matters.
 *
 * Every clause was chosen against a specific failure:
 *
 *  - "This call stores audio" FIRST, present tense. The standing policy is the
 *    durable fact and the current silence is the transient one, so a reader who
 *    takes only the first four words has still taken the true half. It is also
 *    what makes truncation safe wherever this is clipped.
 *  - "while every microphone is off" names the CONDITION, not a moment, so the
 *    sentence cannot be read as a progress report. Compare "starting", which
 *    promises an ending.
 *  - "the moment one is on" — NOT "as soon as anyone speaks". The measured
 *    trigger is an eligible microphone TRACK existing: somebody who joins
 *    unmuted and never says a word is already being captured. Writing "speaks"
 *    would have handed people the wrong mitigation dressed as a privacy notice.
 *  - "by itself" and "nobody is asked first" close the gap the operator report
 *    came from. There is no second prompt and no confirmation, and the ~2.5s
 *    from track to capture is faster than a person can react to the indicator
 *    changing under them.
 *
 * What is deliberately NOT here: the words "not", "off" and "recording" as a
 * claim about the present. "Audio is not being stored" is true at the instant
 * it is read and false a moment later with nothing in between, which is the
 * worst thing a privacy sentence can be.
 */
export const ARMED_AUDIO_CLAUSE =
  'This call stores audio. Nothing is being captured while every microphone is off, and capture starts by itself the moment one is on — nobody is asked first.'

export function recordingStatusSentence(presentation: RecordingPresentation): string {
  switch (presentation) {
    case 'live':
      return 'Audio is being stored.'
    case 'starting':
      return 'Audio storage is starting — treat this as recording.'
    case 'armed':
      return ARMED_AUDIO_CLAUSE
    case 'stopping':
      return 'Audio storage is stopping — it may still be recording.'
    case 'failed':
      return 'Audio storage state is unresolved — treat this as recording.'
    case 'off':
      return 'Audio is not being stored.'
  }
}

/**
 * The armed shape on the audio channel.
 *
 * `active_capture_count` is the count `fenceArmed` refuses to ignore, and
 * `actual === 'audio'` is the ownership claim it refuses to talk over. Nothing
 * here is audio-specific except which fields carry those two facts.
 */
export function audioArmed(policy: RoomAudioRecordingPolicy): boolean {
  if (typeof policy.awaiting_source === 'boolean') return policy.awaiting_source
  return fenceArmed(
    policy,
    policy.desired === 'audio',
    policy.actual === 'audio',
    policy.active_capture_count,
  )
}

/**
 * The audio channel's in-flight words.
 *
 * Two, not three: audio has no `reconfiguring`, because there is nothing to
 * reconfigure — the policy is a single scalar. Video's set is one longer, and
 * that difference is the whole reason the sets are parameters rather than a
 * hard-coded condition.
 */
const AUDIO_IN_FLIGHT: ReadonlySet<string> = new Set(['enabling', 'disabling'])

export function converging(policy: RoomAudioRecordingPolicy): boolean {
  return fenceConverging(policy, AUDIO_IN_FLIGHT)
}

/**
 * The audio row's status words in the two-channel panel.
 *
 * Every non-`off` state has to be readable as "assume it is being stored" on
 * its own, without the colour beside it and without the sentence above it —
 * because a row is exactly the thing people scan instead of reading. That is
 * why none of them is a bare participle: "Stopping" gets heard as "stopped".
 *
 * `armed` is the exception to the rule and the reason the rule is written down.
 * It is the one state where "assume it is being stored" is NOT the claim, so it
 * cannot borrow that wording — but "Not stored" would be a technically-true lie
 * that expires without warning. So it states the RULE in the present tense:
 * scanned on its own, "Stores the moment a mic is on" cannot be read as an
 * all-clear, and it cannot be read as motion either. No ellipsis, no participle,
 * nothing that implies something is under way.
 */
export function audioChannelStatus(presentation: RecordingPresentation): string {
  switch (presentation) {
    case 'live':
      return 'Being stored'
    case 'starting':
      return 'Starting — assume it is being stored'
    case 'armed':
      return 'Stores the moment a mic is on'
    case 'stopping':
      return 'Stopping — it may still be being stored'
    case 'failed':
      return 'Unresolved — assume it is being stored'
    case 'off':
      return 'Not stored'
  }
}

/**
 * How often to re-read the room, given the policy we last saw.
 *
 * There is no server push for recorder progress. `recording_changed` on the
 * SSE stream is published by the BFF when a creator's write commits — that
 * covers "the other participant, or another tab, changed it" — but the
 * recorder attaching to a newly published microphone changes `actual` with no
 * event at all. So a poll is required, and the whole question is when to stop.
 *
 *  - CONVERGING: 1.5s. Measured convergence above was ~1.4s, so this is one
 *    or two reads, not a stream of them.
 *  - DIVERGED BUT STABLE (`desired:audio, actual:none, transition:stable`):
 *    this is the ARMED state, and the window that bounds it was written before
 *    the state had a name. The bound was right and the reason has changed: the
 *    old reason was "after a minute the honest reading is no longer 'still
 *    starting'" — which is now handled by SAYING SO rather than by a timer, and
 *    the state does not change with elapsed time. The window survives because
 *    the first minute after somebody joins is when a microphone is most likely
 *    to appear, so it is worth watching closely and not worth watching closely
 *    forever.
 *  - ARMED, PAST THAT WINDOW: `ARMED_POLL_MS`, NOT the settled baseline. This
 *    is the one change the new state forces on the cadence, and it is a
 *    correctness fix rather than a tuning choice. Every other settled state
 *    changes only in response to something this browser can see coming — a
 *    stop needs a PUT, a failure needs a PUT — so a stale read of one is still
 *    a true read. Armed changes to "audio is being stored" with NO event at
 *    all, so the interval is exactly how long the panel can keep saying nothing
 *    is being captured after that stopped being true. A 60s baseline would make
 *    the least defensible claim on the screen the stalest one. The copy states
 *    the lag as well (`privacy.tsx`); the interval is what keeps it small.
 *  - SETTLED or FAILED: the app-wide baseline, and nothing faster. A terminal
 *    error that polled forever would be a retry loop wearing a disguise.
 */
export const CONVERGING_POLL_MS = 1_500
export const DIVERGED_POLL_MS = 4_000
export const DIVERGED_POLL_WINDOW_MS = 60_000
export const ARMED_POLL_MS = 15_000
export const SETTLED_POLL_MS = 60_000

/**
 * The cadence rule itself, with the channel-specific predicates lifted out.
 *
 * Shared because "poll fast while it moves, slowly but not never while it is
 * legitimately diverged, and at baseline once it has settled or failed" is one
 * decision, not two — and because the video channel has exactly the same
 * unobservable transitions (a recorder attaching to a newly published CAMERA
 * produces no event this browser can see, just as a microphone does not).
 *
 * `restingMs` is the floor a channel drops to once nothing is moving and the
 * diverged window has expired. It defaults to the app-wide baseline; audio
 * passes `ARMED_POLL_MS` when the policy is armed, because that is the only
 * resting state whose claim can be falsified without an event.
 */
export function pollIntervalFor(
  isConverging: boolean,
  isDiverged: boolean,
  divergedForMs: number,
  restingMs: number = SETTLED_POLL_MS,
): number {
  if (isConverging) return CONVERGING_POLL_MS
  if (isDiverged && divergedForMs < DIVERGED_POLL_WINDOW_MS) return DIVERGED_POLL_MS
  return restingMs
}

export function recordingPollInterval(
  policy: RoomAudioRecordingPolicy | undefined,
  divergedForMs: number,
): number {
  if (!policy) return SETTLED_POLL_MS
  return pollIntervalFor(
    converging(policy),
    policy.desired !== policy.actual,
    divergedForMs,
    audioArmed(policy) ? ARMED_POLL_MS : SETTLED_POLL_MS,
  )
}

/* -------------------------------------------------------------------------- */
/* what the creator's control is allowed to be                                 */
/* -------------------------------------------------------------------------- */

/**
 * Everything the control needs to render itself, in one value.
 *
 * Modelled as a tagged union so the impossible combinations cannot be built:
 * there is no "unavailable but also busy", and no state that offers a button
 * without also saying which action it performs.
 */
export type RecordingControlState =
  /** Not the creator. The indicator is still shown; the button is not. */
  | { kind: 'observer' }
  /** Creator, but we have not yet learned whether the backend supports this. */
  | { kind: 'checking' }
  /** The deployment cannot change capture live. `reason` is the upstream's. */
  | { kind: 'unavailable'; reason: string }
  /** Our own request is in flight. */
  | { kind: 'busy'; target: StoredAudioMode }
  /** The controller accepted a change and the recorder has not finished. */
  | { kind: 'converging'; target: StoredAudioMode }
  /** Terminal reconcile failure. The only offered action is an explicit re-drive. */
  | { kind: 'failed'; error: string; redrive: StoredAudioMode }
  /** Idle. `next` is what the button would do. */
  | { kind: 'ready'; next: StoredAudioMode }

export interface RecordingControlInputs {
  policy: RoomAudioRecordingPolicy
  /** Does this browser hold the creator capability at all? */
  canControl: boolean
  /** Capability reason from the snapshot, or rolling-upgrade 409 fallback. */
  unavailableReason: string | null
  /** True until the readable capability field has arrived. No write is sent. */
  probing: boolean
  /** Our own PUT is in flight; the value it is asking for. */
  pending: StoredAudioMode | null
}

export function recordingControlState({
  policy,
  canControl,
  unavailableReason,
  probing,
  pending,
}: RecordingControlInputs): RecordingControlState {
  if (!canControl) return { kind: 'observer' }
  // Ordered by how badly each state would mislead if it were ranked lower.
  // "You cannot do this here" outranks everything: offering a button that is
  // guaranteed to 4xx is worse than offering none.
  if (unavailableReason) return { kind: 'unavailable', reason: unavailableReason }
  if (pending) return { kind: 'busy', target: pending }
  if (converging(policy)) {
    return { kind: 'converging', target: policy.desired }
  }
  if (policy.transition === 'failed') {
    return {
      kind: 'failed',
      error: policy.error ?? 'The recorder could not apply the last change.',
      // A re-drive re-sends the SAME desired value. The control plane treats an
      // identical desired as a no-op that re-enqueues reconcile when the
      // transition is not stable (`repo/recording_policy.py:599-605`), which is
      // exactly "an idempotent retry can re-drive the same revision" from the
      // `error` field's own description. It is offered as a button and never
      // performed automatically: this is a terminal state, and a client that
      // retried it on a timer would hammer a controller that has already said
      // it cannot proceed.
      redrive: policy.desired,
    }
  }
  if (probing) return { kind: 'checking' }
  return { kind: 'ready', next: policy.desired === 'audio' ? 'none' : 'audio' }
}

/** The label the button carries in each state. */
export function recordingActionLabel(state: RecordingControlState): string {
  switch (state.kind) {
    case 'busy':
      return state.target === 'audio' ? 'Asking to start…' : 'Asking to stop…'
    case 'converging':
      return state.target === 'audio' ? 'Recorder is starting…' : 'Recorder is stopping…'
    case 'failed':
      return state.redrive === 'audio' ? 'Try starting again' : 'Try stopping again'
    case 'checking':
      return 'Checking…'
    case 'ready':
      return state.next === 'none' ? 'Stop storing audio' : 'Start storing audio'
    default:
      return ''
  }
}
